import React, { useState } from 'react';
import { Select, Slider, InputNumber, Button } from 'antd';
import { HighlightOutlined } from '@ant-design/icons';
import ControlledIconPicker from './components/ControlledIconPicker';
import ColorField from './components/ColorField';
import { isBuiltInShape } from './components/builtInShapes';
import { SingleSymbologyConfig, SymbologyIcon } from '../../../../types/symbology.types';
import { getFillTypeOptions, getOutlineTypeOptions, lineFillTypeValue, lineOutlineTypeValue, clampLineWeight, MIN_LINE_WEIGHT, MAX_LINE_WEIGHT } from './lineSymbologyOptions';
import '../LayerProperties.scss';

interface CategorySymbologyEditorProps {
  /** Label shown in the editor header (the category display/value). */
  title: string;
  value: SingleSymbologyConfig;
  isPointLayer: boolean;
  onRequestExternalIcon: (source: 'styles' | 'upload') => Promise<{ key: string } | null>;
  /** Apply the draft to this category only. */
  onApply: (config: SingleSymbologyConfig) => void;
  /** Copy the draft to every category (US-101664 Scenario 3). */
  onApplyToAll: (config: SingleSymbologyConfig) => void;
}

/**
 * US-101664 Scenarios 1-4: per-category symbology editor. Edits a local draft
 * so changes only commit on Apply / Apply to All (Cancel discards). Contains
 * the full set of Single symbology controls applicable to the layer geometry.
 */
const CategorySymbologyEditor: React.FC<CategorySymbologyEditorProps> = ({
  title,
  value,
  isPointLayer,
  onRequestExternalIcon,
  onApply,
  onApplyToAll,
}) => {
  const [draft, setDraft] = useState<SingleSymbologyConfig>(value);

  const patch = (p: Partial<SingleSymbologyConfig>) => setDraft((prev) => ({ ...prev, ...p }));
  const setIcon = (icon: SymbologyIcon | null) => setDraft((prev) => ({ ...prev, icon }));

  // Mirror US-101662 Scenario 3: an uploaded image icon disables fill/outline.
  const treatAsImage = isPointLayer && draft.icon != null && !isBuiltInShape(draft.icon.key);
  const colorControlsDisabled = treatAsImage;

  const isLineLayer = !!draft.layerType && draft.layerType.includes('Line');
  const fillTypeOptions = getFillTypeOptions(isLineLayer);
  const outlineTypeOptions = getOutlineTypeOptions(isLineLayer);

  return (
    <div className="lp-symbology__stack lp-category-editor">
      <div className="lp-category-editor__header">{title}</div>

      {isPointLayer && (
        <div className="lp-symbology__card">
          <ControlledIconPicker value={draft.icon} onChange={setIcon} onRequestExternalIcon={onRequestExternalIcon} />
        </div>
      )}

      {!colorControlsDisabled && (
        <div className="lp-symbology__card">
          <div className="lp-symbology__card-header">FILL</div>
          <div className="lp-symbology__field-row">
            <ColorField value={draft.fill} onChange={(hex) => patch({ fill: hex })} />
            <Button
              type="text"
              icon={<HighlightOutlined />}
              className="lp-symbology__brush-btn"
              title="Copy to outline"
              onClick={() => setDraft((prev) => ({ ...prev, outline: prev.fill }))}
            />
            <InputNumber
              className="lp-symbology__opacity-input"
              value={draft.fillOpacity}
              onChange={(v) => patch({ fillOpacity: Number(v) })}
              min={0}
              max={100}
              formatter={(v) => `${v}%`}
              parser={(v) => (v ? Number(String(v).replace('%', '')) : 0) as 0}
            />
          </div>
          <div className="lp-symbology__field-row">
            <span className="lp-symbology__sub-label">FILL TYPE</span>
            <Select
              className="lp-symbology__flex-input"
              placeholder="Solid"
              value={lineFillTypeValue(isLineLayer, draft.fillType) ?? 'solid'}
              onChange={(v) => patch({ fillType: v })}
              options={fillTypeOptions}
            />
          </div>
          {isLineLayer && (
            <div className="lp-symbology__field-row">
              <span className="lp-symbology__sub-label">LINE WEIGHT</span>
              <Slider
                className="lp-symbology__flex-slider"
                min={MIN_LINE_WEIGHT}
                max={MAX_LINE_WEIGHT}
                step={0.5}
                value={draft.lineWeight ?? 2}
                onChange={(v) => patch({ lineWeight: v })}
              />
              <InputNumber
                className="lp-symbology__opacity-input"
                value={draft.lineWeight ?? 2}
                onChange={(v) => patch({ lineWeight: clampLineWeight(v) })}
                min={MIN_LINE_WEIGHT}
                max={MAX_LINE_WEIGHT}
                step={0.5}
                placeholder="pt"
              />
            </div>
          )}
        </div>
      )}

      {!colorControlsDisabled && (
        <div className="lp-symbology__card">
          <div className="lp-symbology__card-header">OUTLINE</div>
          <div className="lp-symbology__field-row">
            <ColorField value={draft.outline} onChange={(hex) => patch({ outline: hex })} />
            <Button
              type="text"
              icon={<HighlightOutlined />}
              className="lp-symbology__brush-btn"
              title="Copy to fill"
              onClick={() => setDraft((prev) => ({ ...prev, fill: prev.outline }))}
            />
            <InputNumber
              className="lp-symbology__opacity-input"
              value={draft.outlineOpacity}
              onChange={(v) => patch({ outlineOpacity: Number(v) })}
              min={0}
              max={100}
              formatter={(v) => `${v}%`}
              parser={(v) => (v ? Number(String(v).replace('%', '')) : 0) as 0}
            />
          </div>
          <div className="lp-symbology__field-row">
            <span className="lp-symbology__sub-label">OUTLINE TYPE</span>
            <Select
              className="lp-symbology__flex-input"
              placeholder="Select Type"
              value={lineOutlineTypeValue(isLineLayer, draft.outlineType) ?? undefined}
              onChange={(v) => patch({ outlineType: v })}
              options={outlineTypeOptions}
            />
          </div>
          <div className="lp-symbology__field-row">
            <span className="lp-symbology__sub-label">OUTLINE WEIGHT</span>
            <Slider
              className="lp-symbology__flex-slider"
              min={0}
              max={100}
              value={draft.outlineWeight}
              onChange={(v) => patch({ outlineWeight: v })}
            />
            <InputNumber
              className="lp-symbology__opacity-input"
              value={draft.outlineWeight}
              onChange={(v) => patch({ outlineWeight: Number(v) })}
              min={0}
              max={100}
              placeholder="pt"
            />
          </div>
        </div>
      )}

      {colorControlsDisabled && (
        <div className="lp-symbology__image-hint">Fill and outline are not configurable when using an uploaded image icon.</div>
      )}

      <div className="lp-category-editor__actions">
        <Button onClick={() => onApplyToAll(draft)}>Apply to All</Button>
        <Button type="primary" onClick={() => onApply(draft)}>
          Apply
        </Button>
      </div>
    </div>
  );
};

export default CategorySymbologyEditor;
